import DownloadInteractionsButton from "./download-interactions-button";
import InteractionModalWithPreviews from "../interaction-modal-with-previews";
import InteractionsTableCount from "./interactions-table-count";
import { Box, BoxContent, BoxHeader, BoxHeading, Table, TableColumns } from "@edgetier/client-components";
import { Button } from "@edgetier/components";
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import { FunctionComponent, memo, useCallback, useMemo, useState } from "react";
import { getInteractions } from "./interactions-table.utilities";
import { IInteraction, Route } from "@edgetier/watchtower-types";
import { IProps } from "./interactions-table.types";
import { ROW_LIMIT } from "./interactions-table.constants";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { showServerError } from "utilities/show-server-error";
import { trackUser } from "utilities/track-user";
import { useInfiniteQuery } from "react-query";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useModal } from "@edgetier/modal";
import { IInteractionsCountQueryOptions } from "./interactions-table-count/interactions-table-count.types";
import { doNothing } from "@edgetier/utilities";
import "./interactions-table.scss";

/**
 * Table of interactions.
 * @param props.areWritingIssuesVisible Are writing issues visible for the interactions in the table.
 * @param props.defaultParameters       Interaction parameters that will only be used if others aren't provided.
 * @param props.children                Interactions table columns.
 * @param props.mandatoryParameters     Interaction parameters that will be used regardless.
 * @param props.title                   Title of the table.
 * @param props.queryOptions            Any options to pass to React Query.
 * @returns
 */
const InteractionsTable: FunctionComponent<IProps> = ({
    areWritingIssuesVisible,
    children,
    defaultParameters = {},
    mandatoryParameters = {},
    queryOptions = {},
    showDownloadButton = true,
    title,
}) => {
    const { openModal, modalProps } = useModal({ onCloseModal: () => setVisibleInteraction(null) });
    const [visibleInteraction, setVisibleInteraction] = useState<IInteraction | null>(null);

    // Get interaction parameters from the URL. Some of these are only for this component so are prefixed.
    const interactionQueryParameters = useInteractionQueryParameters({
        defaults: { ...defaultParameters, limit: ROW_LIMIT },
        prefix: INTERACTIONS_TABLE_PREFIX,
        isPrefixOptional: true,
    });

    // Request interactions matching the required phrases.
    const parameters = {
        ...interactionQueryParameters,
        ...mandatoryParameters,
        includeMessagesPreview:
            typeof interactionQueryParameters.anomalyId !== "string" &&
            typeof mandatoryParameters.anomalyId !== "string",
    };
    const { hasNextPage, data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(
        [Route.Interactions, parameters],
        async ({ pageParam }) => getInteractions(parameters, pageParam),
        { ...queryOptions, getNextPageParam: ({ pagination }) => pagination?.nextPage, onError: showServerError }
    );

    // Combine pages of interactions into one array.
    const interactions = useMemo(() => (data?.pages ?? []).flatMap((interactions) => interactions.items), [data]);

    /**
     * Open an interaction in a modal.
     * @param interaction Selected interaction.
     */
    const openInteractionInModal = (interaction: IInteraction) => {
        setVisibleInteraction(interaction);
        openModal();
    };

    /**
     * Load more interactions if there are some available.
     */
    const requestMoreInteractions = useCallback(() => {
        fetchNextPage();
        trackUser("Load More Interactions");
    }, [fetchNextPage]);

    return (
        <>
            {visibleInteraction !== null && (
                <InteractionModalWithPreviews
                    areWritingIssuesVisible={areWritingIssuesVisible}
                    interaction={visibleInteraction}
                    interactions={interactions}
                    parameters={parameters}
                    loadMoreInteractions={hasNextPage ? requestMoreInteractions : doNothing}
                    {...modalProps}
                />
            )}

            <Box className="interactions-table">
                <BoxHeader>
                    <BoxHeading>
                        {title}{" "}
                        <InteractionsTableCount
                            displayCount={interactions.length}
                            parameters={parameters}
                            // Cast infinite query options as query options for interactions table count
                            queryOptions={queryOptions as IInteractionsCountQueryOptions}
                        />
                    </BoxHeading>
                    {showDownloadButton && <DownloadInteractionsButton parameters={parameters} />}
                </BoxHeader>
                <BoxContent horizontalPadding={interactions.length === 0}>
                    <div className="interactions-table__container">
                        <Table
                            getRowKey={({ interactionId }) => interactionId}
                            isInBox={true}
                            headerIsSticky
                            onRowClick={openInteractionInModal}
                            rows={isLoading ? null : interactions}
                        >
                            <TableColumns>{children}</TableColumns>
                        </Table>

                        {hasNextPage && (
                            <Button
                                className="interactions-table__load-more"
                                icon={faEllipsisV}
                                isLoading={isFetchingNextPage !== false}
                                onClick={requestMoreInteractions}
                            >
                                Load More Interactions
                            </Button>
                        )}
                    </div>
                </BoxContent>
            </Box>
        </>
    );
};

export default memo(InteractionsTable);
